/**
 * C1 — Banner de "nova versão disponível".
 *
 * O service worker registrado em src/index.js entrega aqui via
 * `window.__nexoSWRegistration` (set quando registramos) e via o evento
 * customizado "nexo-sw-update" que disparamos quando detectamos um SW
 * em `waiting`. O componente:
 *
 *   - escuta `nexo-sw-update` no window
 *   - mostra banner no topo
 *   - click em "Recarregar": postMessage({type: "SKIP_WAITING"}) pro SW
 *     em waiting + window.location.reload() depois do controllerchange.
 *
 * Princípio: nunca recarrega sozinho — perderia estado da chat/finance.
 * Quem decide é o user.
 */
import { useCallback, useEffect, useState } from "react";
import { RefreshCw, X } from "lucide-react";


export default function UpdateAvailable() {
  const [waitingRegistration, setWaitingRegistration] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handler = (event) => {
      const reg = event?.detail?.registration || window.__nexoSWRegistration;
      if (reg?.waiting) {
        setWaitingRegistration(reg);
        setDismissed(false);
      }
    };
    window.addEventListener("nexo-sw-update", handler);

    // Se o evento já tinha disparado antes do mount, checa direto.
    if (window.__nexoSWRegistration?.waiting) {
      setWaitingRegistration(window.__nexoSWRegistration);
    }

    // controllerchange = novo SW assumiu — recarregamos pra carregar o
    // bundle novo. Guard: só recarrega se o user pediu (via clique no
    // botão), pra não disparar reload inesperado em outras abas.
    const onControllerChange = () => {
      if (window.__nexoReloadOnControllerChange) {
        window.location.reload();
      }
    };
    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);
    }

    return () => {
      window.removeEventListener("nexo-sw-update", handler);
      if (navigator.serviceWorker) {
        navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
      }
    };
  }, []);

  const reload = useCallback(() => {
    const reg = waitingRegistration;
    if (!reg?.waiting) {
      // Fallback: sem waiting (caso atípico), só recarrega.
      window.location.reload();
      return;
    }
    // Sinaliza pro hook do controllerchange recarregar.
    window.__nexoReloadOnControllerChange = true;
    reg.waiting.postMessage({ type: "SKIP_WAITING" });
  }, [waitingRegistration]);

  if (!waitingRegistration || dismissed) return null;

  return (
    <div
      data-testid="pwa-update-banner"
      /* FaseA T3: respeita notch + status bar do iOS (env()=0 no-op em desktop). */
      className="fixed top-[calc(env(safe-area-inset-top,0px)+8px)] left-1/2 -translate-x-1/2 z-[80] max-w-md w-[calc(100%-1rem)] p-3 rounded-xl border border-brand-cyan/30 shadow-lg flex items-center gap-3"
      style={{ background: "rgba(18, 24, 38, 0.96)", backdropFilter: "blur(8px)" }}
    >
      <RefreshCw className="w-4 h-4 text-brand-cyan shrink-0" />
      <p className="text-caption text-foreground flex-1">
        Nova versão do Nexo disponível.
      </p>
      <button
        type="button"
        onClick={reload}
        data-testid="pwa-update-reload"
        className="px-3 py-1.5 rounded-lg bg-brand-cyan text-background text-sm font-medium hover:bg-brand-cyan/90 transition-colors"
      >
        Recarregar
      </button>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dispensar"
        data-testid="pwa-update-dismiss"
        className="p-1 text-foreground-subtle hover:text-foreground transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
